/**
 * SupplyRequestsSection — الطلبات والمرفقات لطلبات عقود الصيانة وأعمال النظافة.
 * Mirrors RequestsSection logic for land requests.
 */
import * as React from "react";
import type { SupplyRequestListItem } from "../../../services/mosqueDonationService";
import type { HygieneSupplyRequestListItem } from "../../../services/hygieneDonationService";
import type { SupplyRequestFileRow } from "../../../services/supplyRequestFilesService";

type SupplyRow = SupplyRequestListItem | HygieneSupplyRequestListItem;

type Props = {
  requests: SupplyRow[];
  selectedReq: SupplyRow | null;
  onSelect: (r: SupplyRow) => void;
  loadingRequests: boolean;
  stageLabel: (s?: string | null) => React.ReactNode;

  /** عند false يُخفى قسم المرفقات (طلبات الصيانة والنظافة لا تتطلب مستندات) */
  attachmentsRequired?: boolean;

  isDraft: boolean;
  isDirectDonor: boolean;
  hasFinancial: boolean;
  hasPoA: boolean;
  requirementsMet: boolean;
  confirmChecked: boolean;
  onConfirmChange: (v: boolean) => void;
  onSubmitRequest: () => void;
  submittingReq: boolean;
  onToggleEdit: () => void;
  editing: boolean;
  onDelete?: () => void;

  docType: number;
  setDocType: (v: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadBusy: boolean;
  setUploadBusy: (v: boolean) => void;
  uploadErr: string | null;
  setUploadErr: (s: string | null) => void;
  MAX_MB: number;
  doUpload: (file: File, type: number) => Promise<void>;
  filesLoading: boolean;
  files: SupplyRequestFileRow[];
  previews: Record<number, string>;
  downloadByDonor: (f: SupplyRequestFileRow) => Promise<void>;

  submitErr: string | null;
  submitOk: string | null;
  onGoToMyRequests?: () => void;
};

const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;

const docTypeLabel = (v: number) =>
  v === DOC.FinancialCapacity ? "خطاب الملاءة المالية" : "صك التوكيل الشرعي";

const SupplyRequestsSection: React.FC<Props> = (props) => {
  const {
    requests,
    selectedReq,
    onSelect,
    loadingRequests,
    stageLabel,
    attachmentsRequired = true,

    isDraft,
    isDirectDonor,
    hasFinancial,
    hasPoA,
    requirementsMet,
    confirmChecked,
    onConfirmChange,
    onSubmitRequest,
    submittingReq,
    onToggleEdit,
    editing,
    onDelete,

    docType,
    setDocType,
    fileInputRef,
    uploadBusy,
    setUploadErr,
    uploadErr,
    MAX_MB,
    doUpload,
    filesLoading,
    files,
    previews,
    downloadByDonor,

    submitErr,
    submitOk,
    onGoToMyRequests,
  } = props;

  return (
    <>
      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-200 px-4 py-3 font-medium text-neutral-900">
          طلبات المساهمة الحالية
        </div>
        <div className="p-4">
          {!requests && (
            <div className="text-sm text-neutral-500">
              اختر متبرعًا لعرض طلباته
            </div>
          )}
          {requests && loadingRequests && (
            <div className="text-sm text-neutral-500">جاري التحميل...</div>
          )}
          {requests && !loadingRequests && requests.length === 0 && (
            <div className="text-sm text-neutral-500">
              لا توجد طلبات مسجلة لهذا المتبرع لهذه الفرصة
            </div>
          )}

          {requests && !loadingRequests && requests.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-neutral-200 w-full max-w-full">
              <table className="min-w-full text-right text-sm">
                <thead className="bg-neutral-50">
                  <tr className="text-neutral-600">
                    <th className="px-3 py-2 font-medium">رقم الطلب</th>
                    <th className="px-3 py-2 font-medium">رقم المسجد</th>
                    <th className="px-3 py-2 font-medium">اسم المسجد</th>
                    <th className="px-3 py-2 font-medium">التاريخ</th>
                    <th className="px-3 py-2 font-medium">حالة الطلب</th>
                    <th className="px-3 py-2 font-medium">اسم مقدم الطلب</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {requests.map((r) => (
                    <tr
                      key={r.supplyId}
                      className={`${selectedReq?.supplyId === r.supplyId ? "bg-neutral-50" : "bg-white"}`}
                    >
                      <td className="px-3 py-2 text-neutral-900">
                        {r.supplyName ?? r.requestName ?? `DSR-${r.supplyId}`}
                      </td>
                      <td className="px-3 py-2 text-neutral-900">{r.number ?? "-"}</td>
                      <td className="px-3 py-2 text-neutral-900 max-w-[180px]">
                        <span className="block break-words leading-snug" title={r.mosqueName ?? undefined}>{r.mosqueName ?? "-"}</span>
                      </td>
                      <td className="px-3 py-2 text-neutral-700">{r.supplyDate ?? "-"}</td>
                      <td className="px-3 py-2 text-neutral-900">
                        {stageLabel(r.state ?? r.status)}
                      </td>
                      <td className="px-3 py-2 text-neutral-700 max-w-[220px]">
                        <span className="block break-words leading-snug" title={r.partnerName ?? undefined}>
                          {r.partnerName ?? "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="inline-flex items-center rounded-lg border border-primary-300 px-3 py-1.5 text-xs text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          onClick={() => onSelect(r)}
                          title="تحديد"
                          aria-label="تحديد"
                        >
                          <span aria-hidden="true">✓</span>
                          <span className="sr-only">تحديد</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedReq && (
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
            <div className="font-medium text-neutral-900">ملخص الطلب</div>
            <div className="flex gap-2">
              {isDraft && (
                <button
                  type="button"
                  className="inline-flex items-center rounded-xl border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                  onClick={onToggleEdit}
                  title="تعديل"
                >
                  <span aria-hidden="true">✎</span>
                  <span className="sr-only">تعديل</span>
                </button>
              )}
              {isDraft && onDelete && (
                <button
                  type="button"
                  className="inline-flex items-center rounded-xl border border-red-300 px-4 py-2 text-sm text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onClick={onDelete}
                  title="حذف"
                >
                  <span aria-hidden="true">🗑</span>
                  <span className="sr-only">حذف</span>
                </button>
              )}
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="text-xs text-neutral-500">رقم الطلب</div>
                <div className="font-semibold text-neutral-900">
                  {selectedReq.supplyName ?? selectedReq.requestName ?? `DSR-${selectedReq.supplyId}`}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">حالة الطلب</div>
                <div className="font-semibold text-neutral-900">
                  {stageLabel(selectedReq.state ?? selectedReq.status)}
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="md:col-span-2">
                <div className="text-xs text-neutral-500">اسم المسجد</div>
                <div className="font-semibold text-neutral-900">
                  {selectedReq.mosqueName ?? "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">رقم المسجد</div>
                <div className="font-semibold text-neutral-900">{selectedReq.number ?? "-"}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">مقدم الطلب</div>
                <div className="font-semibold text-neutral-900">
                  {selectedReq.partnerName ?? "-"}
                </div>
              </div>
            </div>

            {attachmentsRequired && (
              <div className="mt-4">
                <div className="mb-1 text-xs text-neutral-500">
                  {isDirectDonor
                    ? "المتطلبات للمساهم المباشر"
                    : "المتطلبات للمتبرع غير المباشر"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${
                      hasFinancial ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    خطاب الملاءة المالية — {hasFinancial ? "تم الرفع" : "غير مرفوع"}
                  </span>
                  {!isDirectDonor && (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${
                        hasPoA ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      صك التوكيل الشرعي — {hasPoA ? "تم الرفع" : "غير مرفوع"}
                    </span>
                  )}
                </div>
              </div>
            )}

            {attachmentsRequired && isDraft && editing && (
              <div className="mt-4 rounded-2xl border border-neutral-200 p-4">
                <div className="mb-3 font-semibold text-neutral-900">
                  تحميل المستندات المطلوبة
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
                  <div className="md:col-span-4">
                    <label className="mb-1 block text-sm font-medium text-neutral-700">
                      نوع المستند
                    </label>
                    <select
                      className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 shadow-xs focus:border-sa-500 focus:outline-none focus:ring-2 focus:ring-sa-500"
                      value={docType}
                      onChange={(e) => setDocType(Number(e.target.value))}
                    >
                      <option value={DOC.FinancialCapacity}>
                        {docTypeLabel(DOC.FinancialCapacity)}
                      </option>
                      <option value={DOC.PowerOfAttorney}>
                        {docTypeLabel(DOC.PowerOfAttorney)}
                      </option>
                    </select>
                  </div>
                  <div className="md:col-span-5">
                    <label className="mb-1 block text-sm font-medium text-neutral-700">
                      ملف المستند
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="block w-full cursor-pointer rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm shadow-xs file:me-3 file:rounded-lg file:border-0 file:bg-neutral-100 file:px-3 file:py-2 file:text-sm hover:file:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-sa-500"
                      accept=".pdf,.jpg,.jpeg,.png,image/*"
                      disabled={
                        uploadBusy ||
                        (docType === DOC.FinancialCapacity && hasFinancial) ||
                        (docType === DOC.PowerOfAttorney && hasPoA)
                      }
                      onChange={async (e) => {
                        setUploadErr(null);
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          props.setUploadBusy(true);
                          await doUpload(f, docType);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        } catch {
                          setUploadErr("فشل رفع الملف. يرجى المحاولة مرة أخرى");
                        } finally {
                          props.setUploadBusy(false);
                        }
                      }}
                    />
                    <div className="mt-1 text-xs text-neutral-500">
                      الصيغ المسموح بها: PDF, JPG, PNG (حد أقصى {MAX_MB} ميجابايت)
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                      disabled={
                        uploadBusy ||
                        (docType === DOC.FinancialCapacity && hasFinancial) ||
                        (docType === DOC.PowerOfAttorney && hasPoA)
                      }
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadBusy ? "جاري الرفع..." : "رفع الملف"}
                    </button>
                  </div>
                </div>
                {uploadErr && (
                  <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                    {uploadErr}
                  </div>
                )}
              </div>
            )}

            {attachmentsRequired && (
            <div className="mt-4 rounded-2xl border border-neutral-200 p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-semibold text-neutral-900">المستندات المرفوعة</div>
                <label className="inline-flex items-center gap-2 text-xs text-neutral-600">
                  <input
                    type="file"
                    className="hidden"
                    ref={fileInputRef}
                    accept=".pdf,.jpg,.jpeg,.png,image/*"
                    onChange={async (e) => {
                      setUploadErr(null);
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        props.setUploadBusy(true);
                        await doUpload(f, DOC.FinancialCapacity);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      } catch {
                        setUploadErr("فشل رفع الملف. يرجى المحاولة مرة أخرى");
                      } finally {
                        props.setUploadBusy(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="inline-flex items-center rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadBusy}
                  >
                    {uploadBusy ? "جاري الرفع..." : "إضافة مستند"}
                  </button>
                </label>
              </div>
              {filesLoading ? (
                <div className="text-sm text-neutral-500">جاري التحميل...</div>
              ) : files.length === 0 ? (
                <div className="text-sm text-neutral-500">لا توجد مستندات مرفوعة</div>
              ) : (
                <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        {previews[f.id] ? (
                          <img
                            src={previews[f.id]}
                            alt={f.name || ""}
                            className="h-10 w-10 rounded-lg border border-neutral-200 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg border border-neutral-200 bg-neutral-50" />
                        )}
                        <span className="text-neutral-900">
                          {f.name?.trim() || docTypeLabel(f.documentTypeId)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-700">
                          {docTypeLabel(f.documentTypeId)}
                        </span>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-xl border border-primary-300 px-3 py-1.5 text-xs text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          onClick={() => downloadByDonor(f)}
                        >
                          تحميل
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}

            {submitErr && (
              <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                {submitErr}
              </div>
            )}
            {submitOk && (
              <>
                <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                  {submitOk}
                </div>
                {onGoToMyRequests && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-xl border border-sa-600 px-4 py-2 text-sm font-medium text-sa-700 hover:bg-sa-50"
                      onClick={onGoToMyRequests}
                    >
                      طلباتي
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="mt-6 rounded-2xl border border-neutral-200 bg-neutral-50/80 px-4 py-5">
              <div className="flex flex-col gap-3">
                <label className="flex items-start gap-3 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
                    checked={confirmChecked}
                    onChange={(e) => onConfirmChange(e.target.checked)}
                    disabled={!requirementsMet}
                  />
                  <span>
                    {attachmentsRequired
                      ? "أقر بأن جميع البيانات والمستندات التي تم إدخالها صحيحة"
                      : "أقر بأن جميع البيانات التي تم إدخالها صحيحة"}
                  </span>
                </label>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={onSubmitRequest}
                  disabled={!requirementsMet || !confirmChecked || submittingReq}
                >
                  {submittingReq ? "جاري الإرسال..." : "إرسال الطلب"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupplyRequestsSection;
